// client/src/components/pages/Geofencing/aerialUploadQueue.js
// Module-level upload queue for aerial imagery (ST-00133).
//
// Large surveyor orthos take a while to upload; holding the upload inside the
// Aerials tab component meant any navigation (tab switch, page change)
// unmounted the component and discarded the result. This queue lives at
// module scope, so uploads keep running across SPA navigation and the tab
// re-attaches to live state on return.
//
// Item lifecycle: queued → uploading (pct) → processing → detected | failed,
// then detected → (user reviews & saves) → removed. Items whose upload
// finished ('detected') are persisted to localStorage: the processed file
// already exists on the server, so they stay reviewable even across a full
// page reload (the server's orphan sweep keeps unclaimed uploads for 48h).
// In-flight/queued items cannot survive a reload — the browser kills the
// request — so a beforeunload warning covers that window.
import api from '../../../services/api';

const LS_KEY = 'lexistar.aerialUploadQueue.v1';

let nextId = 1;
let items = [];
let uploading = false;
const listeners = new Set();

// ── persistence (detected-but-unsaved items only) ───────────────────────────

function persist() {
  try {
    const keep = items
      .filter((i) => i.status === 'detected')
      .map(({ id, fileName, fileSize, detection, enqueuedAt }) => ({
        id,
        fileName,
        fileSize,
        detection,
        enqueuedAt,
      }));
    localStorage.setItem(LS_KEY, JSON.stringify(keep));
  } catch (_) {
    /* storage unavailable */
  }
}

(function hydrate() {
  try {
    const stored = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    if (!Array.isArray(stored)) return;
    items = stored
      .filter((s) => s && s.detection && s.fileName)
      .map((s) => ({
        id: nextId++,
        file: null, // original File is gone after a reload; retry not possible
        fileName: s.fileName,
        fileSize: s.fileSize || 0,
        status: 'detected',
        pct: 100,
        detection: s.detection,
        error: null,
        enqueuedAt: s.enqueuedAt || Date.now(),
      }));
  } catch (_) {
    /* corrupt storage — start clean */
  }
})();

// ── subscription ────────────────────────────────────────────────────────────

function snapshot() {
  return { items: items.map((i) => ({ ...i })), uploading };
}

function notify() {
  persist();
  const snap = snapshot();
  listeners.forEach((fn) => fn(snap));
}

/** Subscribe to queue changes; immediately called with the current state. */
export function subscribe(fn) {
  listeners.add(fn);
  fn(snapshot());
  return () => listeners.delete(fn);
}

// ── unload guard while an upload is on the wire ─────────────────────────────

function beforeUnload(e) {
  e.preventDefault();
  e.returnValue = ''; // required for the browser confirm dialog
}

function syncUnloadGuard() {
  const busy = items.some(
    (i) => i.status === 'uploading' || i.status === 'queued' || i.status === 'processing'
  );
  if (busy) window.addEventListener('beforeunload', beforeUnload);
  else window.removeEventListener('beforeunload', beforeUnload);
}

// ── queue operations ────────────────────────────────────────────────────────

/** Add files to the queue and start pumping. Returns the created item ids. */
export function enqueue(files) {
  const ids = [];
  for (const file of files) {
    items.push({
      id: nextId,
      file,
      fileName: file.name,
      fileSize: file.size,
      status: 'queued',
      pct: 0,
      detection: null,
      error: null,
      enqueuedAt: Date.now(),
    });
    ids.push(nextId++);
  }
  syncUnloadGuard();
  notify();
  pump();
  return ids;
}

export function dismiss(id) {
  items = items.filter((i) => i.id !== id);
  syncUnloadGuard();
  notify();
}

/** Re-queue a failed item (only possible while the original File is in memory). */
export function retry(id) {
  const item = items.find((i) => i.id === id);
  if (!item || item.status !== 'failed' || !item.file) return;
  item.status = 'queued';
  item.pct = 0;
  item.error = null;
  syncUnloadGuard();
  notify();
  pump();
}

/** Remove an item after its overlay has been saved. */
export function markSaved(id) {
  dismiss(id);
}

// ── the pump: one upload at a time, in order ────────────────────────────────

async function pump() {
  if (uploading) return;
  const item = items.find((i) => i.status === 'queued');
  if (!item) {
    syncUnloadGuard();
    return;
  }

  uploading = true;
  item.status = 'uploading';
  item.pct = 0;
  notify();

  try {
    const formData = new FormData();
    formData.append('image', item.file);
    const res = await api.aerialOverlays.detectBounds(formData, {
      onUploadProgress: (evt) => {
        const total = evt.total || item.fileSize;
        if (total) {
          const pct = Math.max(1, Math.round((evt.loaded / total) * 100));
          item.pct = pct;
          if (pct >= 100) item.status = 'processing';
          notify();
        }
      },
    });
    const data = res.data?.data;
    if (!data) throw new Error('Empty response from the server');
    item.detection = data;
    item.status = 'detected';
    item.pct = 100;
  } catch (err) {
    item.status = 'failed';
    item.error = describeError(err);
  }

  uploading = false;
  syncUnloadGuard();
  notify();
  pump(); // next in line
}

function describeError(err) {
  if (err?.response?.status === 413) {
    return 'The server rejected the file as too large. Ask an admin to raise the proxy upload limit.';
  }
  if (err?.response?.data?.error) return err.response.data.error;
  if (err?.code === 'ECONNABORTED') {
    return 'The upload timed out. Check your connection and retry — very large files can take several minutes.';
  }
  if (err?.response?.status === 401)
    return 'Your session expired mid-upload. Sign in again and retry.';
  return err?.message || 'Upload failed. Please retry.';
}
